import type { FC, ReactNode } from 'react'
import type { TrackItem } from '@/types'

import { cn } from '@/lib/utils'
import Sparkle from '@/components/landing/decor/sparkle'
import MahoniaSprig from '@/components/landing/decor/mahonia'

interface TrackJacketProps {
    track: TrackItem
    index: number
}

const FALLBACK_COLOR = '#8cc3e3'

const ORBITS = [
    { x: 70, y: 30 },
    { x: 30, y: 36 },
    { x: 64, y: 58 },
    { x: 38, y: 26 },
    { x: 56, y: 44 }
]

const hexToRgb = (hex: string): [number, number, number] => {
    let value = hex.replace('#', '')

    if (value.length === 3) value = value.split('').map(char => char + char).join('')

    const number = Number.parseInt(value, 16)

    return [(number >> 16) & 255, (number >> 8) & 255, number & 255]
}

const mix = (from: string, to: string, amount: number): string => {
    const a = hexToRgb(from)
    const b = hexToRgb(to)
    const channel = (index: number): number => Math.round(a[index] + (b[index] - a[index]) * amount)

    return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`
}

const lightness = (hex: string): number => {
    const [r, g, b] = hexToRgb(hex)

    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
}

// Jacket art drawn from the track's colour, used until a cover image is uploaded in the CMS.
const TrackJacket: FC<TrackJacketProps> = ({ track, index }): ReactNode => {
    const base = track.color ?? FALLBACK_COLOR
    const dark = lightness(base) < 0.45
    const orbit = ORBITS[index % ORBITS.length]
    const line = dark ? 'rgba(251,250,246,0.45)' : 'rgba(29,42,51,0.32)'

    return (
        <div
            className={cn('absolute inset-0 overflow-hidden', dark ? 'text-mist' : 'text-ink')}
            style={{
                background: `radial-gradient(120% 95% at ${orbit.x}% ${orbit.y}%, ${mix(base, '#ffffff', 0.62)} 0%, ${mix(base, '#ffffff', 0.12)} 38%, ${base} 62%, ${mix(base, '#1d2a33', 0.38)} 100%)`
            }}
        >
            <svg viewBox='0 0 100 100' preserveAspectRatio='none' aria-hidden='true' className='absolute inset-0 h-full w-full' fill='none'>
                <circle cx={orbit.x} cy={orbit.y} r='18' stroke={line} strokeWidth='0.35' />
                <circle cx={orbit.x} cy={orbit.y} r='31' stroke={line} strokeWidth='0.3' strokeDasharray='0.6 1.8' />
                <circle cx={orbit.x} cy={orbit.y} r='46' stroke={line} strokeWidth='0.25' />
            </svg>

            <span className='absolute' style={{ left: `${orbit.x}%`, top: `${orbit.y}%` }}>
                <Sparkle className={cn('size-[3.2rem] -translate-x-1/2 -translate-y-1/2 sm:size-16', dark ? 'text-[#ffe7a3]' : 'text-mist')} />
            </span>
            <span className='absolute' style={{ left: `${(orbit.x + 31) % 100}%`, top: `${(orbit.y + 22) % 100}%` }}>
                <Sparkle className='size-3 -translate-x-1/2 -translate-y-1/2 opacity-80' />
            </span>

            <div className={cn('absolute -bottom-[6%] w-[46%] opacity-80', index % 2 === 0 ? '-left-[4%]' : '-right-[4%] -scale-x-100')}>
                <MahoniaSprig variant='flower' seed={60 + index} tone={dark ? 'night' : 'day'} />
            </div>

            <span className='absolute left-[7%] top-[7%] font-mono text-[9px] tracking-[0.34em] opacity-80'>AMAMA NIA</span>
            <span className='absolute right-[7%] top-[7%] font-mono text-[9px] tracking-[0.2em] opacity-70'>
                AMN-{String(index + 1).padStart(3, '0')}
            </span>

            <p
                data-mdlfy-cms={`tracks:${track.id}:title`}
                className='absolute bottom-[8%] right-[7%] max-w-[70%] text-right font-heading text-[clamp(1.4rem,3.4vw,2.6rem)] italic leading-tight tracking-[0.04em]'
            >
                {track.title}
            </p>
        </div>
    )
}

export default TrackJacket
